import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiSave } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import { extractEntity, extractList, isEnabled, champFields } from '../utilities/tournamentsApi';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';

const emptyForm = {
  active: true,
  name: '',
  year: String(new Date().getFullYear()),
  application_limit: '',
  through_limit: '15',
  professional_limit: '',
  canDouble: false,
};

export default function ManageTournamentApplication() {
  const { applicationId } = useParams();
  const isNew = !applicationId;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('tournaments');

  const [form, setForm] = useState(emptyForm);
  const [locked, setLocked] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const returnTo = location.state?.returnTo;
  const seasonDraft = location.state?.seasonDraft;

  useEffect(() => {
    if (!champId && champsLoading) return;
    initPage();
  }, [applicationId, champId, champsLoading]);

  const initPage = async () => {
    setMessage({ text: '', type: '' });
    setErrors({});

    if (isNew) {
      setForm(emptyForm);
      setLocked(false);
      setLoading(false);
      return;
    }

    if (Number(applicationId) === 2) {
      history.replace('/tournaments/applications');
      return;
    }

    if (!champId) {
      setLoading(false);
      setMessage({ text: t('championshipsLoadError'), type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminApplications, { champ_id: champId });
      const list = extractList(response.data);
      const current = list.find((item) => Number(item.id) === Number(applicationId));
      if (!current) {
        setMessage({ text: t('trApplicationNotFound'), type: 'error' });
        setForm(emptyForm);
        return;
      }
      if (Boolean(current.locked) || Number(current.id) === 2) {
        history.replace('/tournaments/applications');
        return;
      }
      setLocked(false);
      setForm({
        active: isEnabled(current.active),
        name: current.name || '',
        year: current.year ? String(current.year) : '',
        application_limit: current.application_limit == null ? '' : String(current.application_limit),
        through_limit: current.through_limit == null || current.through_limit === ''
          ? '15'
          : String(current.through_limit),
        professional_limit: (
          current.professional_unlimited
          || current.professional_limit == null
          || current.professional_limit === ''
        )
          ? ''
          : String(current.professional_limit),
        canDouble: isEnabled(current.canDouble),
      });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = t('stdNameRequired');
    if (!form.year.trim()) nextErrors.year = t('trApplicationYearRequired');
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const goBack = (createdId) => {
    if (returnTo) {
      const [pathname, search] = String(returnTo).split('?');
      history.replace({
        pathname,
        search: search ? `?${search}` : '',
        state: {
          seasonDraft,
          selectedApplicationId: createdId ? String(createdId) : undefined,
        },
      });
      return;
    }
    if (createdId) {
      history.replace(`/tournaments/applications/${createdId}`);
      return;
    }
    history.push('/tournaments/applications');
  };

  const handleSave = async () => {
    if (locked) {
      setMessage({ text: t('trApplicationLocked'), type: 'error' });
      return;
    }
    if (!validate()) return;
    if (!champId) {
      setMessage({ text: t('championshipsLoadError'), type: 'error' });
      return;
    }

    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const throughRaw = form.through_limit === '' ? 15 : Number(form.through_limit);
      const professionalLimit = form.professional_limit === '' || form.professional_limit == null
        ? null
        : Number(form.professional_limit);
      const payload = {
        ...champFields(champId),
        name: form.name.trim(),
        year: form.year.trim(),
        application_limit: form.application_limit === '' ? null : Number(form.application_limit),
        through_limit: Number.isFinite(throughRaw) && throughRaw >= 0 ? throughRaw : 15,
        professional_limit: (
          professionalLimit == null
          || !Number.isFinite(professionalLimit)
          || professionalLimit < 0
        )
          ? null
          : professionalLimit,
        active: form.active ? 1 : 0,
        canDouble: form.canDouble ? 1 : 0,
      };

      if (isNew) {
        const response = await authorizedPost(dispatch, API_URLS.addApplication, payload);
        const created = extractEntity(response.data);
        if (returnTo) {
          goBack(created.id);
          return;
        }
        history.push('/tournaments/applications');
      } else {
        await authorizedPost(dispatch, API_URLS.changeApplication, {
          ...payload,
          id: Number(applicationId),
        });
        setMessage({ text: t('stSaved'), type: 'success' });
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const crumbs = [
    { label: t('navTournaments'), to: '/tournaments' },
    { label: t('trApplications'), to: '/tournaments/applications' },
    { label: isNew ? t('trNewApplication') : (form.name.trim() || t('trEditApplication')) },
  ];

  if (loading) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs items={crumbs} />
            </div>
          </div>
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('trLoading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={crumbs} />
          </div>
        </div>

        <div className="st-scroll">
          <div className="st-content">
            {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}
            {locked ? <div className="st-message error">{t('trApplicationLocked')}</div> : null}

            <div className="st-block">
              <div className="st-form-grid">
                <div className="st-field st-field-wide">
                  <label className="st-check">
                    <input
                      type="checkbox"
                      checked={form.active}
                      disabled={locked}
                      onChange={(event) => updateField('active', event.target.checked)}
                    />
                    {t('trApplicationIsActive')}
                  </label>
                  <span className="st-photo-hint">{t('trApplicationIsActiveHint')}</span>
                </div>

                <div className="st-field">
                  <label>{t('stdName')} <span className="st-required">*</span></label>
                  <input
                    disabled={locked}
                    className={`st-input ${errors.name ? 'error' : ''}`}
                    value={form.name}
                    placeholder={t('trApplicationNamePh')}
                    onChange={(event) => updateField('name', event.target.value)}
                  />
                  {errors.name ? <span className="st-field-error">{errors.name}</span> : null}
                </div>

                <div className="st-field">
                  <label>{t('trApplicationYear')} <span className="st-required">*</span></label>
                  <input
                    disabled={locked}
                    className={`st-input ${errors.year ? 'error' : ''}`}
                    value={form.year}
                    placeholder={t('trApplicationYearPh')}
                    onChange={(event) => updateField('year', event.target.value)}
                  />
                  {errors.year ? <span className="st-field-error">{errors.year}</span> : null}
                </div>

                <div className="st-field">
                  <label>{t('trApplicationLimit')}</label>
                  <input
                    type="number"
                    min="0"
                    disabled={locked}
                    className="st-input"
                    value={form.application_limit}
                    placeholder={t('trApplicationLimitPh')}
                    onChange={(event) => updateField('application_limit', event.target.value)}
                  />
                  <span className="st-photo-hint">{t('trApplicationLimitHint')}</span>
                </div>

                <div className="st-field">
                  <label>{t('trApplicationThroughLimit')}</label>
                  <input
                    type="number"
                    min="0"
                    disabled={locked}
                    className="st-input"
                    value={form.through_limit}
                    placeholder={t('trApplicationThroughLimitPh')}
                    onChange={(event) => updateField('through_limit', event.target.value)}
                  />
                  <span className="st-photo-hint">{t('trApplicationThroughLimitHint')}</span>
                </div>

                <div className="st-field">
                  <label>{t('trApplicationProfessionalLimit')}</label>
                  <input
                    type="number"
                    min="0"
                    disabled={locked}
                    className="st-input"
                    value={form.professional_limit}
                    placeholder={t('trApplicationProfessionalLimitPh')}
                    onChange={(event) => updateField('professional_limit', event.target.value)}
                  />
                  <span className="st-photo-hint">{t('trApplicationProfessionalLimitHint')}</span>
                </div>

                <div className="st-field st-field-wide">
                  <label className="st-check">
                    <input
                      type="checkbox"
                      checked={form.canDouble}
                      disabled={locked}
                      onChange={(event) => updateField('canDouble', event.target.checked)}
                    />
                    {t('trApplicationCanDouble')}
                  </label>
                  <span className="st-photo-hint">{t('trApplicationCanDoubleHint')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="st-actions">
          {returnTo ? (
            <button
              type="button"
              className="st-btn st-btn-secondary"
              disabled={saving}
              onClick={() => goBack()}
            >
              {t('cancel')}
            </button>
          ) : null}
          {canWrite && !locked ? (
          <button
            type="button"
            className="st-btn st-btn-primary"
            disabled={saving}
            onClick={handleSave}
          >
            <FiSave /> {saving ? t('trLoading') : (isNew ? t('trCreateApplication') : t('stdSaveChanges'))}
          </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
